import React, { AnchorHTMLAttributes } from "react";
import classNames from "classnames";
import Link from "next/link";

import { useSelector } from "@/redux/hooks";
import { getCountryCode } from "@/redux/selectors/location";

import CardFlag from "@/components/botanic/Card/CardFlag";
import StarRating from "@/components/botanic/StarRating";
import { BRAND_CATEGORY_IMAGE_MAP } from "@/components/brands/BrandProductCoins/brandProductCategoryImages";
import Image from "@/components/Image";

import ProductCardPrice from "./ProductCardPrice";

type Props = {
  product: ProductCardProduct;
  className?: string;
  disableLazyLoad?: boolean;
  onClick?: () => void;
} & AnchorHTMLAttributes<HTMLAnchorElement>;

export type ProductCardProduct = Required<{
  id: number;
  slug: string;
  avgApprovedPublicReviewRating: number;
  brandName: string;
  brandSlug: string;
  categoryName: string | null;
  cbdContent: string | null;
  countApprovedPublicReviews: number;
  discountLabel: string | null;
  displayPrice: number | null;
  imageUrl: string;
  isBestSeller: boolean;
  isStaffPick: boolean;
  name: string;
  offerLabel: string | null;
  quantity: number | null;
  regularPrice: number | null;
  subcategoryName: string;
  thcContent: string | null;
  unit: string | null;
}>;

const ProductCard = ({
  product,
  className,
  disableLazyLoad,
  onClick,
  ...props
}: Props) => {
  const countryCode = useSelector(getCountryCode);
  const fallbackImage = product.categoryName
    ? BRAND_CATEGORY_IMAGE_MAP[product.categoryName]
    : null;

  const fallbackImageProps = {
    ...(fallbackImage && {
      customImageFallback: fallbackImage,
    }),
  };

  return (
    <Link
      className={classNames(
        "shadow-low rounded overflow-hidden p-lg flex flex-col",
        className,
      )}
      onClick={onClick}
      href={`/brands/${product.brandSlug}/products/${product.slug}`}
      {...props}
    >
      {product.isBestSeller && (
        <CardFlag
          menuCardIndicators={{
            cardFlagText: "Best seller",
            showStaffPick: product.isStaffPick,
          }}
        />
      )}
      {product.imageUrl && (
        <div className="flex justify-center pb-sm">
          <div className="w-[122px]">
            <Image
              src={product.imageUrl}
              sizes={[112]}
              alt={`Product image for ${product.name}`}
              ratio="square"
              disableLazyLoad={disableLazyLoad}
              {...fallbackImageProps}
            />
          </div>
        </div>
      )}
      <div className="flex flex-col pb-xs">
        <div className="flex flex-col justify-between">
          <div className="text-secondary text-xs">
            {product.subcategoryName
              ? product.subcategoryName
              : product.categoryName}
          </div>
          <div className="font-bold text-sm leading-normal">{product.name}</div>
          {product.brandName && (
            <span className="text-secondary text-xs">{`by ${product.brandName}`}</span>
          )}
        </div>
        <div className="flex flex-col gap-0.5">
          {(product.thcContent || product.cbdContent) && (
            <div className="flex flex-row font-bold text-xs gap-sm whitespace-nowrap">
              {product.thcContent && <div>THC {product.thcContent}%</div>}
              {product.cbdContent && <div>CBD {product.cbdContent}%</div>}
            </div>
          )}
          {!!product.avgApprovedPublicReviewRating && (
            <StarRating
              className="flex"
              rating={product.avgApprovedPublicReviewRating}
              ratingCount={product.countApprovedPublicReviews}
              showRating={true}
              starWidth={14}
            />
          )}
        </div>
      </div>
      {product.displayPrice && (
        <ProductCardPrice
          countryCode={countryCode}
          dealDiscountLabel={product.discountLabel}
          displayPrice={product.displayPrice}
          quantity={product.quantity}
          offerLabel={product.offerLabel}
          regularPrice={product.regularPrice}
          unit={product.unit}
        />
      )}
    </Link>
  );
};

export default ProductCard;
